import { useTranslation } from 'react-i18next';

import { SlashedLabel, chipClass, ChipDivider, ChipEvalIcon, CHIP_TEXT_CLASS, UnreachableChip } from './eval-chip';
import { FormulaWithValues } from './formula-field';

import type { TestBarChip } from './collapsed-input-block';
import type { ConditionChips as Chips } from '@/lib/condition-chips';

/**
 * MOVED. The chip data type now lives in `lib/condition-chips.ts`; this file keeps the renderers.
 *
 * The feedback panel has always rendered these as `[chip] and [chip] and [chip]`; the test bar
 * rebuilt the identical list in its own vocabulary. That duplication is why the same state could
 * describe itself two ways ("not 80" against "Any other value"), and why a typo in one surface
 * survived in the other. The shared module could not hold the answer before now because
 * `stateChipLabel` returns a `string`, and a string cannot carry "option A AND option B" or the
 * media icon an MCQ option puts in front of its text — hence `label: ReactNode`, and hence a list.
 *
 * What is NOT here: `id`, `active`, `category`, `track`. Those are the LEGEND's view state — which
 * chip fired, which row it belongs in — and the feedback panel has no notion of them. They go on
 * in `toTestBarChips`, which is the whole difference between the two surfaces.
 */
/**
 * The two RENDERERS. The data type they draw moved to `lib/condition-chips.ts` — see its header for
 * why: `lib/` cannot import `components/`, so a chip list that Flow can also read has to be
 * declared down there, while the things that actually paint pixels belong up here.
 *
 * Re-exported so the blocks that import these names from this module keep working; there is one
 * declaration, in one place, and this is a view of it.
 */
export type { ConditionChip, ConditionChips, ChipRoll } from '@/lib/condition-chips';
export { joinChips, joinChipGroups, chipsToPlainText, chipsToListText, intervalSourceId, activeIntervalIds, answerBarHasContent } from '@/lib/condition-chips';

/**
 * The feedback panel's rendering: chips in a wrapping row, connectors as muted italic words.
 *
 * `full` + a WRAPPING label, which is the one combination that keeps a long condition inside the
 * panel. It was `auto` + `truncate`, and those two cancel out: `auto` sets no maximum, and
 * `truncate` forbids wrapping, so a chip with a lot to say — Default Incorrect listing several
 * carved-out values, most of all — simply grew until the whole panel scrolled sideways.
 *
 * Wrapping rather than truncating, matching what `CHIP_WIDTH` already argues for its capped case:
 * these rows wrap, so a long chip costs a line at worst, while an ellipsis costs the teacher the
 * answer. `full` is what the test-bar legend has always used for the same list.
 */
export function ConditionChipRow({ chips, unreachableLabel }: {
  chips: Chips;
  unreachableLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <div className='flex min-w-0 flex-wrap items-center gap-1.5'>
      {chips.map((c, i) => c.kind === 'connector'
        ? <span key={i} className='shrink-0 text-xs italic text-ws-muted'>{t(`blocks.connectors.${c.word}`)}</span>
        : c.kind === 'unreachable'
        ? <UnreachableChip key={i} label={c.label ?? unreachableLabel} />
        : (
          <span key={i} className={chipClass(c.evaluation, { width: 'full', shrink0: false, italic: c.muted, dimmed: c.muted })}>
            <ChipEvalIcon evaluation={c.evaluation} />
            <ChipDivider />
            <SlashedLabel slashed={c.slashed} className={CHIP_TEXT_CLASS}>{c.label}</SlashedLabel>
            {c.formulas?.map((f, j) => (
              <span key={j} className='shrink-0'>{j > 0 && ', '}<FormulaWithValues formula={f} rolledValues={{}} /></span>
            ))}
            {c.tag && <span className='shrink-0 whitespace-nowrap'>({c.tag})</span>}
          </span>
        ))}
    </div>
  );
}

/**
 * The ANSWER BAR's rendering: PLAIN TEXT, with variable chips where a variable is unresolved.
 *
 * The bar is not a chip surface and never was. I built it as one — `ConditionChipRow` in all seven
 * bar sites — and that was the wrong call: boxes there read as conditions being matched against,
 * when the bar's whole job is to state, in prose, what the setup accepts. Chips belong to the
 * feedback panel and the test legend, where something really is being matched.
 *
 * So this draws the SAME derivation, flattened: connectors as italic words, `formulas` through the
 * app's one chip renderer (a variable has no text form until it is rolled, which is the only reason
 * any chip appears here at all), and `tag` as a trailing parenthetical.
 *
 * TWO empty cases, and they must not read the same:
 *
 *   nothing authored yet   the bar is ABSENT. There is no answer to state, and a block still being
 *                          built should not be told it has no valid answer — its own
 *                          setup-incomplete issue already says where it is up to.
 *   authored, none valid   "No Valid Answer". Something was configured and nothing can satisfy it,
 *                          which is a fact about the setup worth stating in the slot that states
 *                          the answer.
 *
 * The caller decides which by passing `noValidAnswerLabel` only when something IS authored: it
 * holds the setup predicate and this does not.
 */
export function AnswerBarText({ chips, noValidAnswerLabel }: {
  chips: Chips;
  /** Passed ONLY when an answer is authored — see above. */
  noValidAnswerLabel?: string;
}) {
  const { t } = useTranslation();
  const real = chips.filter(c => c.kind === 'chip');
  if (real.length === 0) {
    return noValidAnswerLabel ? <span className='italic opacity-70'>{noValidAnswerLabel}</span> : null;
  }
  return (
    <span className='inline-flex flex-wrap items-baseline gap-x-1.5'>
      {chips.map((c, i) => c.kind === 'connector'
        ? <span key={i} className='italic opacity-70'>{t(`blocks.connectors.${c.word}`)}</span>
        : c.kind === 'unreachable'
        // A dead term inside an otherwise valid reading is dropped here rather than drawn: the bar
        // states what IS accepted, and the issues panel names what is not.
        ? null
        : (
          <span key={i} className={c.muted ? 'italic opacity-60' : undefined}>
            {c.label}
            {c.formulas?.map((f, j) => (
              <span key={j}>{j > 0 && ', '}<FormulaWithValues formula={f} rolledValues={{}} /></span>
            ))}
            {c.tag && <span className='whitespace-nowrap'> ({c.tag})</span>}
          </span>
        ))}
    </span>
  );
}

/**
 * The same list, wearing the legend's envelope.
 *
 * `active` is a property of the STATE — it fired or it did not — so by default it applies to every
 * chip of that state: a fan-out is one condition written across several boxes.
 *
 * `activeSourceIds` is for the blocks where that is not the whole story. Keyword's synonyms, MCQ's
 * options and FITB's per-blank words fan out over ALTERNATIVES, and the legend has always
 * highlighted only the ones the student actually hit. Pass the ids that matched — including a
 * sentinel for "none of the listed ones", which is what makes a catch-all chip light up only when
 * nothing else did — and chips carrying a `sourceId` are tested against that set.
 *
 * A SET rather than a single id, because FITB's Correct is a conjunction across blanks: one word
 * per blank matched, so several chips are legitimately live at once. Chips without a `sourceId`
 * keep the state-wide behaviour, so nothing else has to change.
 */
export function toTestBarChips(chips: Chips, envelope: {
  id: string;
  category: TestBarChip['category'];
  active: boolean;
  track?: string;
  unreachableLabel: string;
  /** Localised "and"/"or" — this is not a component, so it cannot reach i18n itself. */
  connectorWords: Record<'and' | 'or', string>;
  /** Which alternatives the student actually hit. Only consulted for chips that declare a
   *  `sourceId`; `undefined` leaves every chip on the state-wide `active`. */
  activeSourceIds?: ReadonlySet<string>;
}): TestBarChip[] {
  const { id, category, active, track, unreachableLabel, connectorWords, activeSourceIds } = envelope;
  /*
   * A NARROWING THAT SELECTS NOTHING IS NOT A NARROWING -- it is a lost highlight.
   *
   * `activeSourceIds` answers "which of these alternatives did they hit", and the answer is
   * allowed to be "cannot say". `activeIntervalIds` returns an empty set for a value that is not
   * a finite number, so a graded numerical answered `-` (with its unit and uncertainty filled, so
   * the answer is COMPLETE) fired Default Incorrect and lit no chip at all: the state had
   * happened and the legend denied it. Falling back to the state-wide `active` says the row
   * fired without claiming which side of the band it landed on, which is the truth.
   */
  const narrowed = activeSourceIds !== undefined && activeSourceIds.size > 0 ? activeSourceIds : undefined;
  const chipActive = (c: { sourceId?: string }) =>
    active && (c.sourceId === undefined || narrowed === undefined || narrowed.has(c.sourceId));
  return chips.map((c, i) => c.kind === 'connector'
    ? { id: `${id}_c${i}`, condition: connectorWords[c.word], evaluation: 'neutral' as const, active: false, category, kind: 'connector' as const, ...(track ? { track } : {}) }
    : c.kind === 'unreachable'
    ? { id: `${id}_u${i}`, condition: unreachableLabel, evaluation: 'neutral' as const, active: false, reachable: false, category, ...(c.label ? { unreachableLabel: c.label } : {}), ...(track ? { track } : {}) }
    : { id: c.sourceId ? `${id}_${c.sourceId}` : `${id}_${i}`, condition: (c.tag || c.formulas) ? (
        <>
          {c.label}
          {c.formulas?.map((f, j) => (
            <span key={j} className='shrink-0'>{j > 0 && ', '}<FormulaWithValues formula={f} rolledValues={{}} /></span>
          ))}
          {c.tag && <> <span className='shrink-0 whitespace-nowrap'>({c.tag})</span></>}
        </>
      ) : c.label, evaluation: c.evaluation, active: chipActive(c), slashed: c.slashed, muted: c.muted, category, ...(track ? { track } : {}) });
}
